import React, { useEffect, useRef, useState } from 'react';
import { MdSearch, MdKeyboardArrowDown } from 'react-icons/md';
import { getCollegeId, getCollegeLocation, getCollegeName } from '../utils/collegeListing';

const CollegeSearchDropdown = ({
  value,
  onChange,
  onSearch,
  colleges = [],
  placeholder = 'Choose a college...',
  disabled = false,
  className = '',
  excludeIds = []
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);

  const selectedLabel = value ? getCollegeName(value) : placeholder;

  useEffect(() => {
    const localResults = colleges.filter((college) => {
      const matchesSearch = getCollegeName(college).toLowerCase().includes(search.toLowerCase());
      const isExcluded = excludeIds.includes(String(getCollegeId(college)));
      return matchesSearch && !isExcluded;
    });

    if (!onSearch) {
      setResults(localResults);
    }
  }, [colleges, excludeIds, onSearch, search]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !onSearch) {
      return undefined;
    }

    const timeoutId = window.setTimeout(async () => {
      setIsSearching(true);
      try {
        const remoteResults = await onSearch(search);
        setResults(
          (remoteResults || []).filter((college) => !excludeIds.includes(String(getCollegeId(college))))
        );
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [excludeIds, isOpen, onSearch, search]);

  const handleSelect = (college) => {
    onChange(college);
    setIsOpen(false);
    setSearch('');
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div
        onClick={() => !disabled && setIsOpen((open) => !open)}
        className={`w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all cursor-pointer flex items-center justify-between ${
          disabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'hover:border-gray-300 bg-white'
        }`}
      >
        <span className={value ? 'text-gray-900' : 'text-gray-500 truncate'}>
          {selectedLabel}
        </span>
        <MdKeyboardArrowDown className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-80 overflow-hidden">
          <div className="p-3 border-b border-gray-100">
            <div className="relative">
              <MdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search colleges..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
              />
            </div>
          </div>
          <div className="max-h-60 overflow-y-auto">
            {isSearching ? (
              <div className="px-4 py-6 text-center text-gray-500">Searching colleges...</div>
            ) : results.length > 0 ? (
              results.map((college) => (
                <div
                  key={getCollegeId(college) || getCollegeName(college)}
                  onClick={() => handleSelect(college)}
                  className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-50 last:border-b-0 transition-colors"
                >
                  <div className="font-medium text-gray-900">{getCollegeName(college)}</div>
                  <div className="text-sm text-gray-500 mt-1">{getCollegeLocation(college)}</div>
                </div>
              ))
            ) : (
              <div className="px-4 py-6 text-center text-gray-500">
                {search ? 'No colleges found' : 'Type to search colleges'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CollegeSearchDropdown;
